import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Step, Workflow } from './Workflow';
import useFeature from 'hooks/useFeature';
import { selectTenant, FeatureGroups } from 'state/tenant';
import useResponsiveQuery from 'hooks/useResponsiveQuery';
import { selectEnhancedCheckout } from 'state/enhancedCheckout';

const useEnhancedCheckoutWorkflow = () => {
  const { isGroupEnabled } = useFeature();
  const tenant = useSelector(selectTenant);
  const { isMobile } = useResponsiveQuery();
  const { consumerSearchResult } = useSelector(selectEnhancedCheckout);

  const getWorkflow = useCallback((): Workflow => {
    const hasCardChoice = tenant.enabledFeatures.cardChoices.length > 0;
    const hasProductChoice = tenant.enabledFeatures.productChoices.length > 0;
    const hasExistingAccount = consumerSearchResult === 'existingAccount';

    return {
      stepsEnabled: hasExistingAccount
        ? {
            [Step.CONSUMER_SEARCH]: true,
          }
        : {
            [Step.CONSUMER_SEARCH]: true,
            [Step.PRODUCT_CHOICE]: hasProductChoice,
            [Step.CARD_CHOICE]: hasCardChoice,
            [Step.APPLICATION]: true,
            [Step.APPLICATION_2]: isMobile,
            [Step.ADD_ONS]: isGroupEnabled(FeatureGroups['add-ons']),
            [Step.REVIEW_TERMS]: true,
          },

      paths: {
        [Step.CONSUMER_SEARCH]: '/enhanced-checkout',
        [Step.PRODUCT_CHOICE]: '/product-choice',
        [Step.CARD_CHOICE]: '/card-choice',
        [Step.APPLICATION]: '/credit-application',
        [Step.APPLICATION_2]: '/credit-application/2',
        [Step.ADD_ONS]: '/add-ons',
        [Step.REVIEW_TERMS]: '/review-terms',
      },
      progressStartStep: Step.CONSUMER_SEARCH,
      restartStep: Step.CONSUMER_SEARCH,
      timeoutRestartPath: '/enhanced-checkout',
    };
  }, [isGroupEnabled, isMobile, tenant, consumerSearchResult]);

  return getWorkflow;
};

export default useEnhancedCheckoutWorkflow;
